import { useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import { saveAs } from 'file-saver';
import { useDispatch } from 'react-redux';
import CardListTable from 'src/components/cards/CardListTable';
import NewCard from 'src/components/cards/NewCard';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import FilterSelectInput from 'src/common/FilterSelectInput';
import { setErrorInfo } from 'src/redux/modalSlice';
import { BASEAPI } from 'src/api/api';
import { DATE_FORMAT } from 'src/utils/constants';
import moment from 'moment';
import API_URL from 'src/api/apiConstants';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import usePersistentListFilters from 'src/hooks/usePersistentListFilters';

const Cards = ({ location }) => {
  const {
    search: persistedSearch,
    site: persistedSite,
    setSearch: persistSearch,
    setSite: persistSite
  } = usePersistentListFilters();
  const dispatch = useDispatch();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter(persistedSite);

  const [openModal, setOpenModal] = useState(false);
  const [searchText, setSearchText] = useState(persistedSearch);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearch = (value) => {
    setSearchText(value);
    persistSearch(value);
  };

  const handleSiteChange = (value) => {
    setSiteFilter(value);
    persistSite(value);
  };

  const handleExport = () => {
    setShowExportConfirm(false);
    setIsLoading(true);
    BASEAPI.get(`${API_URL.cardList}?export=true`, {
      responseType: 'blob',
      timeout: 20000
    })
      .then((res) => {
        if (res.status !== 200) throw res;

        const fileName = `cards ${moment().format(
          DATE_FORMAT.reportDateTime
        )}.xlsx`;

        saveAs(res.data, fileName);
      })
      .catch(() => {
        dispatch(
          setErrorInfo({
            errorMsg:
              'Error downloading the report. Try again later!'
          })
        );
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <Helmet>
        <title>Cards | Retrack FMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >

          {/* -------- Header -------- */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
              py: 2,
              mb: 2,
              flexShrink: 0
            }}
          >
            {/* Left */}
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#111827'
                }}
              >
                CARDS
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Manage fuel cards, limits and configurations.
              </Typography>
            </Box>

            {/* Right */}
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2,
                flexWrap: 'wrap',
                width: { xs: '100%', md: 'auto' }
              }}
            >
              <TextField
                size="small"
                placeholder="Search Card / Vehicle"
                variant="outlined"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                sx={{
                  width: { xs: '100%', sm: 280, md: 320 },
                  minWidth: 0,
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          color: 'text.secondary'
                        }}
                      />
                    </InputAdornment>
                  )
                }}
              />

              {isAdmin && (
                <FilterSelectInput
                  label="Site"
                  name="site"
                  value={siteFilter}
                  data={siteOptions}
                  onInputChange={handleSiteChange}
                  sx={{ width: { xs: '100%', sm: 220, md: 180 }, mr: 0 }}
                />
              )}

              <Button
                variant="contained"
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                onClick={() => setShowExportConfirm(true)}
                disabled={isLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  height: 40,
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                {isLoading ? 'Exporting...' : 'Export'}
              </Button>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenModal(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  height: 40,
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                Add Card
              </Button>
            </Box>
          </Box>

          {/* -------- Add Card Modal -------- */}
          <NewCard
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />

          <ConfirmationDialog
            open={showExportConfirm}
            info="Are you sure you want to export cards report?"
            actionName="Export"
            onClose={() => setShowExportConfirm(false)}
            onSubmit={handleExport}
          />

          {/* -------- Table -------- */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <CardListTable
              refreshData={location?.state?.fromDashboard}
              searchText={searchText}
              siteFilter={siteFilter}
            />
          </Box>
        </Container>
      </Box>
    </>
  );
};

Cards.propTypes = {
  location: PropTypes.object
};

export default Cards;
